import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { Bq1Builder } from "../components/Bq1Builder";
import { Bq1Classification } from "../components/Bq1Classification";
import { Bq1CommsAndElectronics } from "../components/Bq1CommsAndElectronics";
import { Bq1Dimensions } from "../components/Bq1Dimensions";
import { Bq1GenInfo } from "../components/Bq1GenInfo";
import { Bq1OwnershipAndOperation } from "../components/Bq1OwnershipAndOperation";
import { Bq1RecentOperationalHistory } from "../components/Bq1RecentOperationalHistory";
import { Bq1Tonnages } from "../components/Bq1Tonnages";
import {
  Bq1GeneralInformationM,
  initialBq1GeneralInformation,
  mapSqlToBq1GenInfoForm,
} from "../models/bq1-general-information.model";
import { BqCommVis } from "../models/bq-forms.models";
import { bqF1GeneralInformationAtom } from "../state/baltic-form.atoms";
import { useAtom, useAtomValue } from "jotai";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface Props {
  isCompletedForm: boolean;
  cacheInitialForm: (form: any) => void;
  readonly: boolean;
}

export const Bq1GeneralInformation: React.FC<Props> = ({ isCompletedForm, cacheInitialForm, readonly }) => {
  const theme = useAtomValue(themeAtom);
  const [storedForm, setStoredForm] = useAtom(bqF1GeneralInformationAtom);

  const { imoNumber, vesselName } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<Bq1GeneralInformationM>(initialBq1GeneralInformation);
  const [commentsVisible, setCommentsVisible] = useState<BqCommVis>({});

  function toggleComments(inputId: string) {
    const newComms = { ...commentsVisible };
    newComms[inputId] = !commentsVisible[inputId];

    setCommentsVisible(newComms);
  }

  function isCommentsVisible(inputId: string): boolean {
    return commentsVisible[inputId];
  }

  const updateForm = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const updateDate = (date: Date | null, field: string) => {
    setForm((prevState) => {
      const _form = {
        ...prevState,
        [field]: date !== null ? date.toISOString() : null,
      };

      return _form;
    });
  };

  // Get
  useEffect(() => {
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      fetchData();
    } else {
      if (storageisEmpty) {
        fetchData();
      } else {
        setForm(_form);
        cacheInitialForm(_form);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm]);

  // Storage
  useEffect(() => {
    if (!isLoading) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const fetchData = async () => {
    setIsLoading(true);

    let generalInfoData = [];
    let incidentsData = [];

    try {
      // General Info
      try {
        const res = await api.get(`${apiUrl}/bq_GeneralInfo`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        generalInfoData = res.data[0];
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("General Info not found (404)");
        } else {
          console.error("Error fetching General Info", error);
        }
      }

      // Incidents
      try {
        const res = await api.get(`${apiUrl}/bq_Incidents`, {
          params: { imo_no: imoNumber, vessel_name: vesselName },
        });
        incidentsData = res.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.warn("Incidents not found (404)");
        } else {
          console.error("Error fetching Incidents", error);
        }
      }

      const _form = mapSqlToBq1GenInfoForm(generalInfoData, incidentsData);
      setForm(_form);
      setStoredForm(_form);
      cacheInitialForm(_form);
    } catch (error) {
      console.error("Unexpected error fetching data", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteIncident = async (id: string) => {
    try {
      await api.post(`${apiUrl}/bq_DelIncident`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Incident not found (404)");
      } else {
        console.error("Error deleting Incident", error);
      }
    }
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.inputsBorder,
          theme.balticQuestionnaire.inputsFocusBorder,
          theme.balticQuestionnaire.inputsFocusRing,
          theme.balticQuestionnaire.selectsFocusBorder,
          theme.balticQuestionnaire.selectsFocusRing,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.selectsBorder,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
          theme.balticQuestionnaire.textAreasFocusBorder,
          theme.balticQuestionnaire.textAreasFocusRing,
        )}
      >
        <h1 className={twMerge("text-[24px] font-bold", theme.headerComponent.inline.generalText)}>
          General Information
        </h1>
        <div className="-mt-1 mb-6 pb-2 text-[11px]">Last submitted: {form.balticFormSubmitted}</div>

        <form onSubmit={handleSubmit}>
          {/* Forms */}
          {Bq1GenInfo(form, updateForm, updateDate, toggleComments, isCommentsVisible, readonly)}
          {Bq1CommsAndElectronics(form, updateForm, readonly)}
          {Bq1OwnershipAndOperation(form, updateForm, updateDate, readonly)}
          {Bq1Builder(form, updateDate, updateForm, readonly)}
          {Bq1Classification(form, updateForm, updateDate, toggleComments, isCommentsVisible, readonly)}
          {Bq1Dimensions(form, updateForm, toggleComments, isCommentsVisible, readonly)}
          {Bq1Tonnages(form, updateForm, toggleComments, isCommentsVisible, readonly)}
          {Bq1RecentOperationalHistory(form, updateForm, setForm, deleteIncident, readonly)}
          <br />
        </form>
      </div>
    </LoadingWrapper>
  );
};
